import {Injectable, inject} from '@angular/core';
import {HttpClient} from '@angular/common/http';
import {Capacitor} from '@capacitor/core';
import {CapacitorReferrer} from '@leochaddad/capacitor-referrer';
import {StorageService} from 'src/app/services/storage.service';
import {LoggerService} from 'src/app/services/logger.service';
import {environment} from 'src/environments/environment';

export type AdClickParam = 'gclid' | 'gbraid' | 'wbraid';

export interface StoredAdClick {
  param: AdClickParam;
  clickId: string;
  capturedAt: string;
  sent: boolean;
}

const AD_CLICK_KEY = 'ad_click';
const AD_REFERRER_CHECKED_KEY = 'ad_referrer_checked';
// Precedence when more than one identifier is present: gclid > gbraid > wbraid.
const AD_CLICK_PARAMS: AdClickParam[] = ['gclid', 'gbraid', 'wbraid'];
const CLICK_ID_PATTERN = /^[A-Za-z0-9_-]{1,255}$/;

/**
 * Captures the Google Ads click identifier (gclid/gbraid/wbraid) from either
 * the Android Play Store install referrer (first launch only) or a deep link,
 * and forwards it to the API once the user is signed in so the server can
 * store it on the customer for offline conversion upload (ROAS).
 *
 * Storage-only, no dependency on AuthService: AuthService calls flush() after
 * it persists a session, which keeps this service safe to inject from
 * AuthService without introducing a DI cycle.
 */
@Injectable({
  providedIn: 'root',
})
export class AdAttributionService {
  private http = inject(HttpClient);
  private storage = inject(StorageService);
  private logger = inject(LoggerService);

  private extractClickId(params: URLSearchParams): {param: AdClickParam; clickId: string} | null {
    for (const param of AD_CLICK_PARAMS) {
      const value = params.get(param);
      if (value && CLICK_ID_PATTERN.test(value)) {
        return {param, clickId: value};
      }
    }
    return null;
  }

  private async storeClick(param: AdClickParam, clickId: string): Promise<void> {
    const click: StoredAdClick = {
      param,
      clickId,
      capturedAt: new Date().toISOString(),
      sent: false,
    };
    await this.storage.set(AD_CLICK_KEY, click);
  }

  /**
   * Parses a deep-link URL for gclid/gbraid/wbraid and stores the first match
   * found (precedence gclid > gbraid > wbraid). A newer click always
   * overwrites a previously stored, unsent click.
   */
  async captureFromUrl(url: string): Promise<void> {
    try {
      const parsed = new URL(url);
      const found = this.extractClickId(parsed.searchParams);
      if (!found) {
        return;
      }
      await this.storeClick(found.param, found.clickId);
      this.logger.logInformation('Ad click captured from deep link', {param: found.param});
    } catch (error) {
      this.logger.logError(`Ad attribution captureFromUrl error: ${error?.message ?? error}`);
    }
  }

  /**
   * Reads the Android Play Install Referrer exactly once (first launch),
   * guarded by ad_referrer_checked so we never re-query the Play Store. Any
   * failure is swallowed and logged — attribution capture must never break
   * app startup.
   */
  async captureFromInstallReferrer(): Promise<void> {
    try {
      if (Capacitor.getPlatform() !== 'android') {
        return;
      }
      const alreadyChecked = await this.storage.get(AD_REFERRER_CHECKED_KEY);
      if (alreadyChecked) {
        return;
      }
      try {
        const details = await CapacitorReferrer.getReferrerDetails();
        const referrerUrl = details?.referrerUrl;
        if (referrerUrl) {
          // referrerUrl is a URL-encoded query string (e.g.
          // "gclid=Cj0K...&utm_source=google-play"), not a full URL.
          const params = new URLSearchParams(referrerUrl);
          const found = this.extractClickId(params);
          if (found) {
            await this.storeClick(found.param, found.clickId);
            this.logger.logInformation('Ad click captured from install referrer', {
              param: found.param,
            });
          }
        }
      } catch (error) {
        this.logger.logError(
          `Ad attribution install referrer read error: ${error?.message ?? error}`
        );
      } finally {
        // Set regardless of outcome so we never re-query the Play Store.
        await this.storage.set(AD_REFERRER_CHECKED_KEY, true);
      }
    } catch (error) {
      this.logger.logError(`Ad attribution captureFromInstallReferrer error: ${error?.message ?? error}`);
    }
  }

  /**
   * Sends a stored, unsent click to the API. Never throws — a failure just
   * leaves the click unsent so the next sign-in retries it.
   */
  async flush(): Promise<void> {
    try {
      const stored: StoredAdClick | undefined = await this.storage.get(AD_CLICK_KEY);
      if (!stored || stored.sent) {
        return;
      }
      await this.http
        .post(environment.API_URL + 'user/ad-click', {
          param: stored.param,
          clickId: stored.clickId,
        })
        .toPromise();
      await this.storage.set(AD_CLICK_KEY, {...stored, sent: true});
    } catch (error) {
      this.logger.logError(`Ad attribution flush error: ${error?.message ?? error}`);
    }
  }
}
